import { CustomAttr, Service } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { Button, TextInput, Loader, Select, Textarea, Autocomplete } from '@mantine/core';
import { IconClock, IconExternalLink } from '@tabler/icons-react';
import { ServiceTitleStatic } from '../services/service-title';
import { useState, useMemo } from 'react';
import { useUpdateService } from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';
import { PRICE_BY_OPTIONS, SERVICE_CATEGORIES } from '@turizi/shared';
import { Link } from 'react-router-dom';
import { SERVICES_PATH } from '@/lib/navigation';
import { updateAttrValue } from '@/lib/models-utils';

interface PropertiesTabProps {
    services: Service[];
}

interface ServiceUpdateStatus {
    id: Service['id'];
    loading: boolean;
    success: boolean | null;
}

const DELAY = 350; // ms

export function BulkEditPropertiesTab({ services }: PropertiesTabProps) {
    const { t } = useTranslation();

    const [defaultLimit, setDefaultLimit] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [observations, setObservations] = useState<string>('');
    const [updateStatuses, setUpdateStatuses] = useState<Record<Service['id'], ServiceUpdateStatus>>({});
    const [priceBy, setPriceBy] = useState<string>('');
    const [costBy, setCostBy] = useState<string>('');
    const [category, setCategory] = useState<string>('');

    const [selfAttrUpdates, setSelfAttrUpdates] = useState<Record<string, string>>({});
    const [bookingAttrUpdates, setBookingAttrUpdates] = useState<Record<string, string>>({});

    const selfAttrDefinitions = useMemo(() => {
        const map = new Map<string, CustomAttr & { existingValues?: string[] }>();
        services.forEach(service => {
            service.self_attrs?.forEach(attr => {
                if (attr.key) {
                    if (!map.has(attr.key)) {
                        map.set(attr.key, { ...attr, existingValues: [] });
                    }
                    if (attr.value !== undefined && attr.value !== null && attr.value !== '') {
                        const valStr = String(attr.value);
                        const existing = map.get(attr.key)!;
                        if (!existing.existingValues!.includes(valStr)) {
                            existing.existingValues!.push(valStr);
                        }
                    }
                }
            });
        });
        return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
    }, [services]);

    const bookingAttrDefinitions = useMemo(() => {
        const map = new Map<string, CustomAttr & { existingValues?: string[] }>();
        services.forEach(service => {
            service.booking_attrs?.forEach(attr => {
                if (attr.key) {
                    if (!map.has(attr.key)) {
                        map.set(attr.key, { ...attr, existingValues: [] });
                    }
                    if (attr.value !== undefined && attr.value !== null && attr.value !== '') {
                        const valStr = String(attr.value);
                        const existing = map.get(attr.key)!;
                        if (!existing.existingValues!.includes(valStr)) {
                            existing.existingValues!.push(valStr);
                        }
                    }
                }
            });
        });
        return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
    }, [services]);


    const [counter, setCounter] = useState<number>(0);
    const hasStarted = Object.keys(updateStatuses).length > 0;
    const allCompleted = hasStarted && Object.values(updateStatuses).every(status => !status.loading);

    const updateServiceMutation = useUpdateService();

    const getUpdates = (service: Service): Partial<Service> => {
        const updates: Partial<Service> = {
            // Build the update object with only changed fields
        };
        if (defaultLimit !== '') {
            updates.default_limit = Number(defaultLimit);
        };
        if (city !== '') {
            updates.city = city;
        };
        if (observations !== '') {
            updates.observations = observations;
        };
        if (priceBy !== '') {
            updates.price_by = priceBy as Service['price_by'];
        };
        if (costBy !== '') {
            updates.cost_by = costBy as Service['cost_by'];
        };
        if (category !== '') {
            updates.category = category as Service['category'];
        };

        let newSelfAttrs = service.self_attrs ? [...service.self_attrs] : [];
        let selfAttrsModified = false;
        Object.entries(selfAttrUpdates).forEach(([key, value]) => {
            if (value !== '') {
                newSelfAttrs = updateAttrValue(newSelfAttrs, key, value);
                selfAttrsModified = true;
            }
        });
        if (selfAttrsModified) {
            updates.self_attrs = newSelfAttrs;
        }

        let newBookingAttrs = service.booking_attrs ? [...service.booking_attrs] : [];
        let bookingAttrsModified = false;
        Object.entries(bookingAttrUpdates).forEach(([key, value]) => {
            if (value !== '') {
                newBookingAttrs = updateAttrValue(newBookingAttrs, key, value);
                bookingAttrsModified = true;
            }
        });
        if (bookingAttrsModified) {
            updates.booking_attrs = newBookingAttrs;
        }

        return updates;
    }

    const handleBulkUpdate = async () => {
        // Initialize statuses
        const initialStatuses: Record<Service['id'], ServiceUpdateStatus> = {};
        services.forEach(service => {
            initialStatuses[service.id] = { id: service.id, loading: true, success: null };
        });
        setUpdateStatuses(initialStatuses);
        setCounter(0);

        // Update each service
        let completed = 0;
        for (let i = 0; i < services.length; i++) {
            const service = services[i];

            if (i > 0) {
                await new Promise(resolve => setTimeout(resolve, DELAY));
            }

            try {
                await updateServiceMutation.mutateAsync({
                    id: service.id,
                    ...getUpdates(service),
                });

                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [service.id]: { id: service.id, loading: false, success: true }
                }));
            } catch (error) {
                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [service.id]: { id: service.id, loading: false, success: false }
                }));
            }
        }
    };

    return (
        <>
            <div className="space-y-4">

                <div className='flex gap-2'>
                    <TextInput
                        label={t('default_limit')}
                        // placeholder={t('enter_default_limit')}
                        value={defaultLimit}
                        onChange={(e) => setDefaultLimit(e.target.value)}
                        type="number"
                    />
                    <TextInput
                        label={t('city')}
                        // placeholder={t('enter_city')}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                    />
                </div>

                <div className='flex gap-2 solid-box-border'>
                    <Select
                        label={t('price_by')}
                        className="font-bold w-fit"
                        data={PRICE_BY_OPTIONS.map(option => ({
                            label: t(option.label),
                            value: option.value,
                        }))}
                        onChange={(value) => {
                            setPriceBy(value as any);
                        }}
                    />
                    <Select
                        label={t('cost_by')}
                        className="font-bold w-fit"
                        data={PRICE_BY_OPTIONS.map(option => ({
                            label: t(option.label),
                            value: option.value,
                        }))}
                        onChange={(value) => {
                            setCostBy(value as any);
                        }}
                    />
                </div>

                <Textarea
                    label={t('observations')}
                    // placeholder={t('enter_observations')}
                    value={observations}
                    onChange={(e) => setObservations(e.target.value)}
                    minRows={3}
                    autosize
                />

                {selfAttrDefinitions.length > 0 && (
                    <div className="space-y-2 solid-box-border">
                        <div className="text-sm font-semibold">{t('self_attrs')}</div>
                        <div className="flex flex-wrap gap-2">
                            {selfAttrDefinitions.map(attr => (
                                attr.options && attr.options.length > 0 ? (
                                    <Select
                                        key={attr.key}
                                        label={t(attr.key)}
                                        // placeholder={t('no_change')}
                                        data={attr.options.map(opt => ({ label: opt, value: opt }))}
                                        value={selfAttrUpdates[attr.key] || null}
                                        onChange={(val) => setSelfAttrUpdates(prev => ({ ...prev, [attr.key]: val || '' }))}
                                        clearable
                                    />
                                ) : (
                                    <Autocomplete
                                        key={attr.key}
                                        label={t(attr.key)}
                                        // placeholder={t('no_change')}
                                        value={selfAttrUpdates[attr.key] || ''}
                                        onChange={(val) => setSelfAttrUpdates(prev => ({ ...prev, [attr.key]: val }))}
                                        data={attr.existingValues || []}
                                    />
                                )
                            ))}
                        </div>
                    </div>
                )}

                {bookingAttrDefinitions.length > 0 && (
                    <div className="space-y-2 solid-box-border">
                        <div className="text-sm font-semibold">{t('booking_attrs')}</div>
                        <div className="flex flex-wrap gap-2">
                            {bookingAttrDefinitions.map(attr => (
                                attr.options && attr.options.length > 0 ? (
                                    <Select
                                        key={attr.key}
                                        label={t(attr.key)}
                                        // placeholder={t('no_change')}
                                        data={attr.options.map(opt => ({ label: opt, value: opt }))}
                                        value={bookingAttrUpdates[attr.key] || null}
                                        onChange={(val) => setBookingAttrUpdates(prev => ({ ...prev, [attr.key]: val || '' }))}
                                        clearable
                                    />
                                ) : (
                                    <Autocomplete
                                        key={attr.key}
                                        label={t(attr.key)}
                                        // placeholder={t('no_change')}
                                        value={bookingAttrUpdates[attr.key] || ''}
                                        onChange={(val) => setBookingAttrUpdates(prev => ({ ...prev, [attr.key]: val }))}
                                        data={attr.existingValues || []}
                                    />
                                )
                            ))}
                        </div>
                    </div>
                )}

                <div className="space-y-4 solid-box-border-green text-xs max-h-96 overflow-y-auto mt-10">
                    {services?.map(service => (
                        <Link
                            key={service.id}
                            to={`${SERVICES_PATH}/${service.id}`}
                            target="_blank"
                            className="block text-xs flex items-start gap-1 mb-2"
                        >
                            <IconExternalLink size={16} />
                            <div className="flex-1">
                                {ServiceTitleStatic(service)}
                            </div>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="mt-8">
                {!hasStarted ? (
                    <div className="flex justify-end">
                        <Button
                            onClick={handleBulkUpdate}
                            color="black"
                            radius="xl"
                            size="xs"
                        >
                            {t('apply_changes')}
                        </Button>
                    </div>
                ) : allCompleted ? (
                    <div className="flex items-center justify-center gap-2">
                        <div className="text-sm font-medium text-green-600">
                            {t('done')}
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2">
                        <div className="text-sm font-medium">
                            {t('updating')} {counter} / {services.length}
                        </div>
                        <Loader size="sm" color="black" />
                    </div>
                )}
            </div>
        </>
    );
}
